import * as React from 'react';
import { TextField, Button, TextSelect, NumberSelect } from '../common';
import { BaseDialog, BaseDialogProps, BaseDialogState } from '../common/dialog/BaseDialog';
import { Grid2 as Grid } from '@mui/material';
import { getVersionsViews } from '../../utils';

interface Props extends BaseDialogProps {
    readonly selectedClientVersion: Entities.ClientVersion | null;
    readonly cloudCodeRevisions: Entities.CloudCode[];
    readonly titleDataVersions: Entities.TitleData[];
    readonly contentVersions: Entities.FileVersion[];
    readonly onUpdate: (clientVersion: Entities.ClientVersion) => void;
}

interface State extends BaseDialogState {
    readonly cloudCodeRevision: number;
    readonly titleDataVersion: string;
    readonly contentVersion: string;
    readonly forceUpdate: boolean;
}

export class UpdateClientVersionDialog extends BaseDialog<Props, State> {
    state = this.initState();

    componentDidUpdate(prevProps: Props) {
        if (this.props.selectedClientVersion !== prevProps.selectedClientVersion) {
            this.setState(this.initState());
        }
    }

    protected renderContent(): React.JSX.Element {
        const { selectedClientVersion, cloudCodeRevisions, titleDataVersions, contentVersions } = this.props;
        const { cloudCodeRevision, titleDataVersion, contentVersion } = this.state;

        const versionsViews = getVersionsViews(cloudCodeRevisions, titleDataVersions, contentVersions, true);
        return (
            <Grid container={true} spacing={0}>
                <Grid size={12}>
                    <TextField
                        label="Version"
                        fullWidth={true}
                        value={selectedClientVersion ? selectedClientVersion.version : ''}
                        required={true}
                        disabled={true}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={12}>
                    <NumberSelect
                        label="Cloud Code"
                        value={cloudCodeRevision}
                        onChange={this.handleCloudCodeRevisionChange}
                    >
                        {versionsViews.cloudCodesViews}
                    </NumberSelect>
                </Grid>
                <Grid size={12}>
                    <TextSelect
                        label="Title Data"
                        value={titleDataVersion}
                        onChange={this.handleTitleDataVersionChange}
                    >
                        {versionsViews.titleDataVersionsViews}
                    </TextSelect>
                </Grid>
                <Grid size={12}>
                    <TextSelect
                        label="Content"
                        value={contentVersion}
                        onChange={this.handleContentVersionChange}
                    >
                        {versionsViews.contentVersionsViews}
                    </TextSelect>
                </Grid>
            </Grid>
        );
    }

    protected renderActions(): React.JSX.Element {
        return (
            <Button text="Update" onClick={this.onUpdate} variant="contained" />
        );
    }

    protected initState(): State {
        const { selectedClientVersion } = this.props;
        if (!selectedClientVersion) {
            return {
                cloudCodeRevision: -1,
                titleDataVersion: "",
                contentVersion: "",
                forceUpdate: false,
            };
        }

        return {
            ...selectedClientVersion,
            cloudCodeRevision: selectedClientVersion.cloudCodeRevision || -1,
            titleDataVersion: selectedClientVersion.titleDataVersion || '',
            contentVersion: selectedClientVersion.contentVersion || '',
            forceUpdate: selectedClientVersion.forceUpdate || false,
        };
    }

    protected handleCloudCodeRevisionChange = (cloudCodeRevision: number) => this.setState({ cloudCodeRevision });

    protected handleTitleDataVersionChange = (titleDataVersion: string) => this.setState({ titleDataVersion });

    protected handleContentVersionChange = (contentVersion: string) => this.setState({ contentVersion });

    private onUpdate = () => {
        const { selectedClientVersion } = this.props;
        if (selectedClientVersion == null) {
            return;
        }

        const { cloudCodeRevision, titleDataVersion, contentVersion, forceUpdate } = this.state;
        const clientVersion: Entities.ClientVersion = {
            version: selectedClientVersion.version,
            live: selectedClientVersion.live,
            cloudCodeRevision,
            titleDataVersion,
            contentVersion,
            forceUpdate,
            updated: 0,
        };
        this.props.onUpdate(clientVersion);
        this.onClose();
    };
}
